import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { StaffShell } from "@/components/layout/StaffShell";
import { vendorNav } from "@/lib/nav";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ImageUpload } from "@/components/common/ImageUpload";
import { CenterSpinner } from "@/components/common/States";

const STATUS_TONE = {
  approved: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]",
  pending_approval: "bg-[hsl(var(--warning))]/20 text-[hsl(27_60%_32%)]",
  suspended: "bg-destructive/10 text-destructive",
  rejected: "bg-destructive/10 text-destructive",
};

export default function VendorStore() {
  const qc = useQueryClient();
  const vendor = useQuery({ queryKey: ["my-vendor"], queryFn: () => api("/my/vendor") });
  const cats = useQuery({ queryKey: ["categories"], queryFn: () => api("/categories") });
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isNew = vendor.isFetched && !vendor.data;

  useEffect(() => {
    if (vendor.data) setForm(vendor.data);
    else if (isNew && !form)
      setForm({ name: "", category: "", description: "", phone: "", whatsapp_number: "", logo_url: null, banner_url: null, latitude: -33.9321, longitude: 18.8602, delivery_fee_base: 25, eta_minutes: 30 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendor.data, isNew]);

  if (vendor.isLoading || !form) {
    return <StaffShell items={vendorNav} roleLabel="Vendor" title="My store"><CenterSpinner /></StaffShell>;
  }

  const save = async () => {
    setBusy(true);
    try {
      if (isNew) {
        await api("/vendors", {
          method: "POST",
          json: { ...form, latitude: Number(form.latitude), longitude: Number(form.longitude), delivery_fee_base: Number(form.delivery_fee_base), eta_minutes: Number(form.eta_minutes) },
        });
        toast.success("Store submitted for approval");
      } else {
        await api("/my/vendor", {
          method: "PATCH",
          json: {
            name: form.name, description: form.description, category: form.category, phone: form.phone,
            whatsapp_number: form.whatsapp_number, logo_url: form.logo_url, banner_url: form.banner_url,
            latitude: Number(form.latitude), longitude: Number(form.longitude),
            delivery_fee_base: Number(form.delivery_fee_base), eta_minutes: Number(form.eta_minutes),
          },
        });
        toast.success("Store updated");
      }
      qc.invalidateQueries({ queryKey: ["my-vendor"] });
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const toggleLive = async (v) => {
    try {
      const res = await api("/my/vendor", { method: "PATCH", json: { is_active: v } });
      setForm(res);
      qc.invalidateQueries({ queryKey: ["my-vendor"] });
      toast.success(v ? "You are live!" : "Store paused");
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <StaffShell
      items={vendorNav}
      roleLabel="Vendor"
      title="My store"
      headerRight={<Button className="rounded-full" onClick={save} disabled={busy} data-testid="save-store-button">{isNew ? "Submit" : "Save"}</Button>}
    >
      <div className="mx-auto max-w-2xl space-y-5">
        {!isNew && (
          <Card className="flex items-center justify-between rounded-2xl p-5">
            <div>
              <div className="flex items-center gap-2">
                <span className="font-display text-lg">{form.name}</span>
                <Badge className={`rounded-full font-normal ${STATUS_TONE[form.status] || ""}`}>{(form.status || "").replace("_", " ")}</Badge>
              </div>
              <p className="text-sm text-muted-foreground">
                {form.status === "approved" ? "Toggle to accept orders." : "Store must be approved before going live."}
              </p>
            </div>
            <Switch checked={!!form.is_active} disabled={form.status !== "approved"} onCheckedChange={toggleLive} data-testid="go-live-switch" />
          </Card>
        )}

        <Card className="grid gap-4 rounded-2xl p-5 sm:grid-cols-2">
          <div>
            <Label className="mb-2 block">Logo</Label>
            <ImageUpload value={form.logo_url} onChange={(v) => set("logo_url", v)} label="Add logo" aspect="aspect-square" testId="store-logo-upload" />
          </div>
          <div>
            <Label className="mb-2 block">Banner</Label>
            <ImageUpload value={form.banner_url} onChange={(v) => set("banner_url", v)} label="Add banner" testId="store-banner-upload" />
          </div>
        </Card>

        <Card className="space-y-4 rounded-2xl p-5">
          <div className="space-y-1.5">
            <Label>Store name</Label>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="store-name-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Category</Label>
            <Select value={form.category} onValueChange={(v) => set("category", v)}>
              <SelectTrigger data-testid="store-category-select"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                {cats.data?.map((c) => <SelectItem key={c.category_id} value={c.name}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Description</Label>
            <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Phone</Label><Input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>WhatsApp</Label><Input value={form.whatsapp_number || ""} onChange={(e) => set("whatsapp_number", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Latitude</Label><Input type="number" step="any" value={form.latitude} onChange={(e) => set("latitude", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Longitude</Label><Input type="number" step="any" value={form.longitude} onChange={(e) => set("longitude", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Delivery fee (R)</Label><Input type="number" value={form.delivery_fee_base} onChange={(e) => set("delivery_fee_base", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>ETA (min)</Label><Input type="number" value={form.eta_minutes} onChange={(e) => set("eta_minutes", e.target.value)} /></div>
          </div>
        </Card>
      </div>
    </StaffShell>
  );
}
